/**
 * Sales Team Management Types
 * Hierarchy, positions, territory assignments, and account reviews
 */

import type { UUID } from "./common.types";

// Position levels in the sales hierarchy
export type SalesPositionType =
  | "vp"
  | "director"
  | "regional_manager"
  | "territory_manager"
  | "sales_rep";

// Display labels for position types
export const POSITION_TYPE_LABELS: Record<SalesPositionType, string> = {
  vp: "VP of Sales",
  director: "Director",
  regional_manager: "Regional Manager",
  territory_manager: "Territory Manager",
  sales_rep: "Sales Rep",
};

// Position type hierarchy order (0 = top)
export const POSITION_TYPE_ORDER: Record<SalesPositionType, number> = {
  vp: 0,
  director: 1,
  regional_manager: 2,
  territory_manager: 3,
  sales_rep: 4,
};

// Account review material status options
export type MaterialStatus = "yes" | "no" | "na";
export const MATERIAL_STATUS_OPTIONS = [
  { value: "yes", label: "Yes" },
  { value: "no", label: "No" },
  { value: "na", label: "N/A" },
];

// Account review statuses
export type ReviewStatus =
  | "draft"
  | "submitted"
  | "needs_revision"
  | "approved";
export const REVIEW_STATUS_OPTIONS = [
  { value: "draft", label: "Draft", color: "gray" },
  { value: "submitted", label: "Submitted", color: "blue" },
  { value: "needs_revision", label: "Needs Revision", color: "yellow" },
  { value: "approved", label: "Approved", color: "green" },
];

// =====================================================
// SALES POSITION
// =====================================================

export interface SalesPosition {
  id: UUID;
  title: string;
  position_type: SalesPositionType;
  parent_position_id: UUID | null;

  // Assigned person
  assigned_profile_id: UUID | null;
  assigned_name: string | null;
  assigned_email: string | null;

  // Territory / region
  territory: string | null;
  region: string | null;
  states: string[] | null;

  // Employment
  hire_date: string | null;
  position_start_date: string | null;
  is_part_time: boolean;
  is_active: boolean;

  notes: string | null;
  // Promotion auto-assign engine opt-in (meaningful on director rows). Default false
  // → engine dormant until toggled. See promotion_state_rules / set_director_auto_assign.
  auto_assign_promotions_enabled?: boolean;
  created_at: string;
  updated_at: string;
  created_by: UUID | null;
  updated_by: UUID | null;

  // Joined data (populated via select queries)
  parent_position?: SalesPosition;
  assigned_profile?: {
    id: UUID;
    full_name: string;
    email: string;
  };
  children?: SalesPosition[];
  territory_assignments?: SalesTerritoryAssignment[];
  // Computed stats
  direct_report_count?: number;
  organization_count?: number;
}

export interface CreateSalesPositionInput {
  title: string;
  position_type: SalesPositionType;
  parent_position_id?: UUID;
  assigned_profile_id?: UUID;
  assigned_name?: string;
  assigned_email?: string;
  territory?: string;
  region?: string;
  states?: string[];
  hire_date?: string;
  position_start_date?: string;
  is_part_time?: boolean;
  is_active?: boolean;
  notes?: string;
}

export interface UpdateSalesPositionInput {
  title?: string;
  position_type?: SalesPositionType;
  parent_position_id?: UUID | null;
  assigned_profile_id?: UUID | null;
  assigned_name?: string | null;
  assigned_email?: string | null;
  territory?: string | null;
  region?: string | null;
  states?: string[] | null;
  hire_date?: string | null;
  position_start_date?: string | null;
  is_part_time?: boolean;
  is_active?: boolean;
  notes?: string | null;
}

// =====================================================
// TERRITORY ASSIGNMENTS
// =====================================================

export interface SalesTerritoryAssignment {
  id: UUID;
  sales_position_id: UUID;
  organization_id: UUID;
  is_primary: boolean;
  assigned_at: string;
  assigned_by: UUID | null;
  notes: string | null;
  created_at: string;
  updated_at: string;

  // Joined data
  organization?: {
    id: UUID;
    name: string;
    organization_type?: string;
    address_city?: string;
    address_state?: string;
  };
  sales_position?: {
    id: UUID;
    title: string;
    assigned_name?: string;
  };
}

export interface CreateTerritoryAssignmentInput {
  sales_position_id: UUID;
  organization_id: UUID;
  is_primary?: boolean;
  notes?: string;
}

// =====================================================
// ACCOUNT REVIEWS
// =====================================================

export interface AccountReview {
  id: UUID;
  organization_id: UUID;
  submitted_by: UUID | null;
  sales_position_id: UUID | null;
  review_date: string;

  // FreshService
  freshservice_ticket_created: boolean;

  // Other Items
  evidence_of_competitor_materials: boolean;

  // Retailer Materials
  counter_cards_on_display: MaterialStatus | null;
  acrylic_stand_in_good_shape: MaterialStatus | null;
  posters: MaterialStatus | null;
  banners: MaterialStatus | null;
  dealershield_window_cling: MaterialStatus | null;

  // Technology and Software
  counter_card_qr_verification: boolean;
  classroom_posters_qr_verification: boolean;
  promo_code_qr_verification: boolean;
  affiliate_link_on_website: boolean;
  marketing_videos_playing: boolean;
  uses_gearfire_axis_pos: boolean;

  // Events and Opportunities
  attending_classes: boolean;
  usls_certified_instructors: boolean;

  // Comment
  review_comment: string | null;

  // Status
  status: ReviewStatus;
  reviewed_by: UUID | null;
  reviewed_at: string | null;
  reviewer_id: UUID | null;
  last_edited_by: UUID | null;
  last_edited_at: string | null;

  created_at: string;
  updated_at: string;

  // Joined data
  organization?: {
    id: UUID;
    name: string;
  };
  submitted_by_profile?: {
    id: UUID;
    full_name: string;
  };
  reviewer_profile?: {
    id: UUID;
    full_name: string;
  };
  last_edited_by_profile?: {
    id: UUID;
    full_name: string;
  };
  sales_position?: {
    id: UUID;
    title: string;
  };
}

export interface ReviewComment {
  id: UUID;
  review_id: UUID;
  user_id: UUID;
  comment_text: string;
  created_at: string;
  user_profile?: {
    id: UUID;
    full_name: string;
  };
}

export interface CreateAccountReviewInput {
  organization_id: UUID;
  sales_position_id?: UUID;
  review_date?: string;
  freshservice_ticket_created?: boolean;
  evidence_of_competitor_materials?: boolean;
  counter_cards_on_display?: MaterialStatus;
  acrylic_stand_in_good_shape?: MaterialStatus;
  posters?: MaterialStatus;
  banners?: MaterialStatus;
  dealershield_window_cling?: MaterialStatus;
  counter_card_qr_verification?: boolean;
  classroom_posters_qr_verification?: boolean;
  promo_code_qr_verification?: boolean;
  affiliate_link_on_website?: boolean;
  marketing_videos_playing?: boolean;
  uses_gearfire_axis_pos?: boolean;
  attending_classes?: boolean;
  usls_certified_instructors?: boolean;
  review_comment?: string;
  status?: ReviewStatus;
}

export interface UpdateAccountReviewInput
  extends Partial<CreateAccountReviewInput> {
  reviewed_by?: UUID;
  reviewed_at?: string;
}

// =====================================================
// HIERARCHY TREE NODE (for UI rendering)
// =====================================================

export interface SalesHierarchyNode {
  position: SalesPosition;
  children: SalesHierarchyNode[];
  depth: number;
  isExpanded?: boolean;
}

// =====================================================
// FILTERS
// =====================================================

export interface SalesPositionFilters {
  position_type?: SalesPositionType[];
  region?: string;
  is_active?: boolean;
  search?: string;
}

export interface AccountReviewFilters {
  organization_id?: UUID;
  submitted_by?: UUID;
  status?: ReviewStatus[];
  date_from?: string;
  date_to?: string;
  search?: string;
}
